import { useEffect, useReducer, useRef, useState } from 'react';
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@roger/ui-components';
import { MoreHorizontal, Pencil, SmilePlus, Trash2 } from 'lucide-react';
import type { AttachmentUrl } from '../../hooks/chat/useAttachmentUrls';
import { composerInputId } from '../../hooks/chat/useConversation';
import type { AttachmentOut, MessageOut } from '../../services/api';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { PREVIEW_MAX_BYTES } from '../../utils/attachments';
import { AttachmentChip } from './AttachmentChip';
import { AttachmentTile } from './AttachmentTile';
import { MOTION } from './chatMotion';
import { EmojiPicker } from './EmojiPicker';
import { initialsOf } from './initials';
import { mentionsUser } from './mentions';
import { MessageEditor } from './MessageEditor';
import { MessageText } from './MessageText';

type ReactionOut = NonNullable<MessageOut['reactions']>[number];

const isPreviewKind = (a: AttachmentOut) =>
  a.content_type.startsWith('image/') || a.content_type === 'application/pdf';

/**
 * The resting width the bubble column gives up, on the side the hover bar
 * takes, so a bubble that reaches the row edge never sits under the bar. It is
 * the bar's own width plus 6px of clearance: one 32px icon button on a pointer
 * device or 44px on a phone, the 4px gap and the 1px borders. Tailwind emits a
 * class only when it can read it as literal text, so this is a table and not a
 * computed arbitrary value.
 */
const BAR_ZONE: Record<string, string> = {
  'own-1': 'ml-[40px]',
  'own-2': 'ml-[76px]',
  'own-1-touch': 'ml-[52px]',
  'own-2-touch': 'ml-[100px]',
  'other-1': 'mr-[40px]',
  'other-2': 'mr-[76px]',
  'other-1-touch': 'mr-[52px]',
  'other-2-touch': 'mr-[100px]',
};

/** The spec's render table, in its order. */
// eslint-disable-next-line react-refresh/only-export-components -- the render table sits beside the one component that uses it
export function attachmentRender(
  a: AttachmentOut
): 'tile' | 'tile-pending' | 'chip' | 'chip-no-preview' {
  if (!isPreviewKind(a)) return 'chip';
  if (a.status === 'ready') return 'tile';
  if (a.status === 'failed' || a.size_bytes > PREVIEW_MAX_BYTES)
    return 'chip-no-preview';
  return 'tile-pending';
}

/** "You" first, then the others in the order they reacted. */
const reactorNames = (reaction: ReactionOut, currentUserId: string): string =>
  [
    ...(reaction.reactors.some(r => r.user_id === currentUserId)
      ? ['You']
      : []),
    ...reaction.reactors
      .filter(r => r.user_id !== currentUserId)
      .map(r => r.name),
  ].join(', ');

/** The server owns the window; this mirror only hides the control on time. */
const EDIT_WINDOW_MS = 5 * 60_000;

/**
 * True while the message is still inside the edit window, computed on every
 * render. One timeout forces a render at the deadline, so the control goes
 * away even when nothing else re-renders the row. A message that is not
 * changeable, or already out of time, sets no timer.
 */
function useEditWindowOpen(createdAt: string, enabled: boolean): boolean {
  const deadline = new Date(createdAt).getTime() + EDIT_WINDOW_MS;
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const open = enabled && Date.now() < deadline;
  useEffect(() => {
    if (!enabled) return;
    if (Date.now() >= deadline) {
      // The deadline passed between the render's clock read and this effect:
      // force one render so the control goes away. After that render `open`
      // is false, so this branch cannot fire again.
      if (open) forceRender();
      return;
    }
    const timer = setTimeout(forceRender, deadline - Date.now());
    return () => clearTimeout(timer);
  }, [deadline, enabled, open]);
  return open;
}

export function MessageBubble({
  message,
  isOwn,
  showSender,
  urls = {},
  onOpenAttachment,
  onDownloadAttachment = () => {},
  currentUserId = '',
  touch = false,
  canReact = false,
  selected = false,
  onSelect = () => {},
  pendingReaction = false,
  onToggleReaction = () => {},
  canChange = false,
  onDelete,
  onEdit,
  arrival,
  historyReady = true,
}: {
  message: MessageOut;
  isOwn: boolean;
  showSender: boolean;
  urls?: Record<string, AttachmentUrl | null | undefined>;
  onOpenAttachment?: (tiles: AttachmentOut[], index: number) => void;
  onDownloadAttachment?: (id: string) => void;
  currentUserId?: string;
  /** Phone sheet: a tap selects the bubble, and tap targets are 44px. */
  touch?: boolean;
  /** False when the channel is archived: chips are read-only, no add control. */
  canReact?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  /** A reaction write for this message is in flight. */
  pendingReaction?: boolean;
  onToggleReaction?: (emoji: string, reacted: boolean) => void;
  /** True when the channel takes writes: the sender may edit or delete. */
  canChange?: boolean;
  /** Delete this message. Rejects on a failure so the row can react to 409. */
  onDelete?: () => Promise<unknown>;
  /** Save a new body. Rejects on a failure so the editor can react to 409. */
  onEdit?: (body: string) => Promise<unknown>;
  /**
   * `'live'` for a message that arrived after history settled, `'sent'` for
   * one this list showed as a pending row. Absent for history.
   */
  arrival?: 'live' | 'sent';
  /**
   * The channel's history has settled, the same signal `MessageList` reads.
   * Defaults true for a bubble rendered on its own, whose first render is the
   * one that records the counts its chips start from.
   */
  historyReady?: boolean;
}) {
  const attachments = message.attachments ?? [];
  // The lightbox moves across this message's ready tiles only.
  const tiles = attachments.filter(a => attachmentRender(a) === 'tile');
  const mentionsReader =
    !isOwn && !!currentUserId && mentionsUser(message.body, currentUserId);
  const reactions = message.reactions ?? [];
  // 'live' and 'sent' are the two things a row can be doing; history is neither.
  const arrivalClass =
    arrival === 'live' ? MOTION.enter : arrival === 'sent' ? MOTION.settle : '';
  const [sideOpen, setSideOpen] = useState(false);
  const [chipOpen, setChipOpen] = useState(false);
  const removed = !!message.removed_at;
  const windowOpen = useEditWindowOpen(message.created_at, isOwn && canChange);
  const [mode, setMode] = useState<'view' | 'confirm' | 'edit'>('view');
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const pickedRef = useRef(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  // The confirmation outlives the window, so an Escape after the deadline
  // still cancels. The server's 409 is the real limit.
  const changeable = isOwn && canChange && !removed && windowOpen;

  // preventScroll: a default focus scroll can push the wrapped confirmation's
  // Delete button below the list fold at 390 and defeat the row-growth follow.
  useEffect(() => {
    if (mode === 'confirm') cancelRef.current?.focus({ preventScroll: true });
  }, [mode]);

  // A closed row returns focus to the composer on desktop; on the phone the
  // keyboard stays down.
  const closeChangeRow = () => {
    setMode('view');
    if (!touch)
      document.getElementById(composerInputId(message.channel_id))?.focus();
  };

  const confirmDelete = async () => {
    if (deleting || !onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
      closeChangeRow();
    } catch (error) {
      // Archived or out of time: the server refused, so close the row.
      if (getHttpStatus(error) === 409) closeChangeRow();
    } finally {
      setDeleting(false);
    }
  };

  // The editor's onSave never rejects: a 409 closes it here, another failure
  // leaves the text so the person can retry or cancel.
  const saveEdit = async (body: string) => {
    if (!onEdit) return;
    try {
      await onEdit(body);
      closeChangeRow();
    } catch (error) {
      if (getHttpStatus(error) === 409) closeChangeRow();
    }
  };

  // Removal wins over an open editor or confirmation: a tombstone that
  // arrives by refetch must replace the stale row. The accepted
  // deadline-only persistence is untouched, since only removal overrides it.
  const editing = mode === 'edit' && !removed;
  // Each chip's committed count, replaced after every commit once the first
  // settled snapshot creates the map. `historyReady` gates only that creation:
  // a later refetch turns it false again, and a commit during that fetch must
  // still update the baseline, so a stale count cannot replay archive motion or
  // hide a real count change. An unchanged count never restarts the pop.
  // Recording in a commit effect, never during render, keeps a discarded render
  // from changing what a later committed render reads, and holding exactly the
  // current chips forgets one that disappeared, so a same-count reappearance
  // pops again.
  const committedCountsRef = useRef<Map<string, number> | null>(null);
  useEffect(() => {
    if (committedCountsRef.current === null && !historyReady) return;
    committedCountsRef.current = new Map(
      reactions.map(reaction => [reaction.emoji, reaction.reactors.length])
    );
  });
  // The one place that guards writes: a pending message ignores a second one.
  const toggle = (emoji: string, reacted: boolean) => {
    if (!pendingReaction) onToggleReaction(emoji, reacted);
  };
  // aria-disabled, not disabled, so the control keeps focus while pending.
  const openIfIdle = (setOpen: (open: boolean) => void) => (open: boolean) => {
    if (!open || !pendingReaction) setOpen(open);
  };
  const chipClass = (mine: boolean) =>
    `inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs ${
      mine ? 'border-primary bg-primary/10' : 'bg-background'
    } ${touch ? 'min-h-11 min-w-11 justify-center' : ''}`;

  // The controls a row could show right now, independent of the phone's
  // selection gate: which controls render and whether the bar is revealed read
  // these live.
  const reactionEligible = canReact && !removed;
  const optionsEligible = changeable && !deleting;
  const showsReactionControl = reactionEligible && (!touch || selected);
  const showsOptionsControl = optionsEligible && (!touch || selected);
  // The phone's selection gate decides only whether the bar is shown.
  const showsControlsBar =
    !removed &&
    !editing &&
    (reactionEligible || optionsEligible) &&
    (!touch || selected);
  // The bar's resting zone: the most slots this row has ever committed, never
  // fewer than it could show now. A deadline, a pending delete, a live archive
  // or a selection change never shrinks the resting width, so the bubble keeps
  // its line breaks. A row first mounted after its deadline starts at whatever
  // it can still show. The high-water mark is published in an effect, never
  // during render, so a discarded render cannot widen a later committed one.
  const committedSlotsRef = useRef(0);
  const zoneSlots = Math.max(
    committedSlotsRef.current,
    Number(reactionEligible) + Number(optionsEligible)
  );
  useEffect(() => {
    if (zoneSlots > committedSlotsRef.current)
      committedSlotsRef.current = zoneSlots;
  });
  const barZone =
    zoneSlots > 0
      ? BAR_ZONE[
          `${isOwn ? 'own' : 'other'}-${zoneSlots}${touch ? '-touch' : ''}`
        ]
      : '';

  const reactionControl = showsReactionControl && (
    <EmojiPicker
      open={sideOpen}
      onOpenChange={openIfIdle(setSideOpen)}
      touch={touch}
      onSelect={native => {
        toggle(native, true);
        setSideOpen(false);
      }}
      trigger={
        <Button
          type='button'
          variant='ghost'
          size={touch ? 'iconLg' : 'iconMd'}
          aria-label='Add reaction'
          aria-disabled={pendingReaction}
          className='rounded-full'
        >
          <SmilePlus className='h-4 w-4' />
        </Button>
      }
    />
  );

  // Only the sender's own, live message gets the menu, inside the write
  // window and while the channel takes writes.
  const optionsControl = showsOptionsControl && (
    <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size={touch ? 'iconLg' : 'iconMd'}
          aria-label='Message options'
          className='rounded-full'
        >
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        className={MOTION.reduceRadix}
        // Radix returns focus to the trigger on close, and its items refocus
        // the menu while it animates out, so a pick must claim focus again
        // here -- at Radix's last focus action -- or a late steal leaves the
        // row with focus on the page body. Escape with no pick still returns
        // focus to the trigger, as a menu should.
        onCloseAutoFocus={event => {
          if (mode === 'confirm') {
            event.preventDefault();
            cancelRef.current?.focus({ preventScroll: true });
          } else if (pickedRef.current) {
            event.preventDefault();
          }
          pickedRef.current = false;
        }}
      >
        {message.body && onEdit && (
          <DropdownMenuItem
            onSelect={() => {
              pickedRef.current = true;
              setMode('edit');
            }}
          >
            <Pencil className='mr-2 h-4 w-4' />
            Edit
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() => {
            pickedRef.current = true;
            setMode('confirm');
          }}
        >
          <Trash2 className='mr-2 h-4 w-4' />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Both hover controls sit in one bar, top-aligned and next to the bubble
  // itself (left of an own bubble, right of another's), never at the row edge:
  // a short bubble leaves the row mostly empty. Anchoring it inside the bubble
  // box keeps it clear of the column's resting gutter (BAR_ZONE) and off a
  // wider chip row or sender line. `absolute` keeps the row's layout fixed,
  // and the row is the `group`, so focus within still reveals the bar.
  const controlsBar = showsControlsBar && (
    <div
      className={`absolute top-0 ${isOwn ? 'right-full mr-1' : 'left-full ml-1'} flex items-center gap-1 rounded-lg border bg-background text-foreground shadow-sm ${
        touch
          ? ''
          : `opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100 ${
              sideOpen || menuOpen || chipOpen ? 'opacity-100' : ''
            }`
      }`}
      onClick={event => event.stopPropagation()}
    >
      {reactionControl}
      {optionsControl}
    </div>
  );

  // The row wraps at narrow widths. Escape cancels without minimizing the
  // window: the chat keyboard listener skips a prevented Escape.
  const confirmRow = mode === 'confirm' && !removed && (
    <div
      className='flex flex-wrap items-center gap-2'
      onClick={event => event.stopPropagation()}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          if (!deleting) closeChangeRow();
        }
      }}
    >
      <span className='text-xs text-muted-foreground'>
        Delete this message?
      </span>
      <Button
        ref={cancelRef}
        type='button'
        variant='ghost'
        size={touch ? 'touch' : 'sm'}
        disabled={deleting}
        onClick={closeChangeRow}
      >
        Cancel
      </Button>
      <Button
        type='button'
        variant='destructive'
        size={touch ? 'touch' : 'sm'}
        disabled={deleting}
        onClick={() => void confirmDelete()}
      >
        Delete
      </Button>
    </div>
  );

  return (
    <div
      data-seq={message.seq}
      className={`group relative flex gap-2 px-3 py-0.5 ${isOwn ? 'justify-end' : ''} ${arrivalClass}`}
    >
      {!isOwn && (
        <Avatar
          size='sm'
          initials={initialsOf(message.sender_name)}
          className='mt-auto h-6 w-6 shrink-0'
        />
      )}
      <div
        className={`flex min-w-0 flex-col ${
          editing ? 'w-full max-w-none' : `max-w-[86%] ${barZone}`
        } ${isOwn ? 'items-end' : 'items-start'}`}
      >
        {showSender && (
          <span className='mb-0.5 text-[11px] font-semibold'>
            {message.sender_name}
          </span>
        )}
        {editing && onEdit ? (
          <MessageEditor
            body={message.body ?? ''}
            touch={touch}
            onSave={saveEdit}
            onCancel={closeChangeRow}
          />
        ) : (
          <div
            data-chat-bubble
            onClick={event => {
              if (touch && event.currentTarget.contains(event.target as Node))
                onSelect();
            }}
            className={`relative min-w-0 max-w-full px-3 py-2 text-sm leading-5 ${
              removed
                ? `border bg-background italic text-muted-foreground ${
                    isOwn
                      ? 'rounded-[12px_12px_4px_12px]'
                      : 'rounded-[12px_12px_12px_4px]'
                  }`
                : `${
                    isOwn
                      ? 'rounded-[12px_12px_4px_12px] bg-primary-600 text-primary-foreground'
                      : 'rounded-[12px_12px_12px_4px] bg-muted text-foreground'
                  } ${mentionsReader ? 'ring-2 ring-amber-500' : ''}`
            } ${touch && zoneSlots > 0 ? 'min-h-12' : ''}`}
          >
            {isOwn && controlsBar}
            {removed ? (
              <p>This message was deleted</p>
            ) : (
              <>
                {message.body && (
                  <p
                    data-testid='message-body'
                    className='whitespace-pre-wrap [overflow-wrap:anywhere]'
                  >
                    <MessageText
                      body={message.body}
                      currentUserId={currentUserId}
                      isOwn={isOwn}
                    />
                  </p>
                )}
                {attachments.length > 0 && (
                  <div
                    className={`flex flex-wrap gap-2 ${message.body ? 'mt-1' : ''}`}
                  >
                    {attachments.map(a => {
                      const kind = attachmentRender(a);
                      if (kind === 'chip' || kind === 'chip-no-preview') {
                        return (
                          <AttachmentChip
                            key={a.id}
                            attachment={a}
                            noPreview={kind === 'chip-no-preview'}
                            onDownload={onDownloadAttachment}
                          />
                        );
                      }
                      return (
                        <AttachmentTile
                          key={a.id}
                          attachment={a}
                          url={urls[a.id]}
                          own={isOwn}
                          onOpen={
                            kind === 'tile' && onOpenAttachment
                              ? () => onOpenAttachment(tiles, tiles.indexOf(a))
                              : undefined
                          }
                          onDownload={onDownloadAttachment}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
            {!isOwn && controlsBar}
          </div>
        )}
        {confirmRow}
        {!removed && reactions.length > 0 && (
          <div
            className={`mt-1 flex flex-wrap gap-1 ${isOwn ? 'justify-end' : ''}`}
            onClick={event => event.stopPropagation()}
          >
            {/* The adder sits to the left of the chips, as it does in Slack. */}
            {canReact && (
              <EmojiPicker
                open={chipOpen}
                onOpenChange={openIfIdle(setChipOpen)}
                touch={touch}
                onSelect={native => {
                  toggle(native, true);
                  setChipOpen(false);
                }}
                trigger={
                  <button
                    type='button'
                    aria-label='Add reaction'
                    aria-disabled={pendingReaction}
                    className={chipClass(false)}
                  >
                    <SmilePlus className='h-3.5 w-3.5' />
                  </button>
                }
              />
            )}
            {reactions.map(reaction => {
              const mine = reaction.reactors.some(
                r => r.user_id === currentUserId
              );
              const count = reaction.reactors.length;
              const label = `${reaction.emoji} ${count} reaction${count === 1 ? '' : 's'}${mine ? ', you reacted' : ''}`;
              // A chip pops when its count differs from the committed one, or
              // when the emoji is new — but only once the history snapshot has
              // settled. A count that arrives while `historyReady` is false (a
              // refetch) stays still; the effect above still records it, so the
              // settled render does not replay it. The committed map holds
              // exactly the last committed emojis, so a chip that disappeared
              // and a same-count chip that reappears both look new and pop again.
              const committed = committedCountsRef.current;
              const pops =
                historyReady &&
                committed !== null &&
                committed.get(reaction.emoji) !== count;
              return (
                <Tooltip key={reaction.emoji}>
                  <TooltipTrigger asChild>
                    {canReact ? (
                      <button
                        type='button'
                        aria-label={`${label}. Toggle`}
                        aria-pressed={mine}
                        aria-disabled={pendingReaction}
                        className={chipClass(mine)}
                        onClick={() => toggle(reaction.emoji, !mine)}
                      >
                        {/* Keyed by count, so a changed count remounts only this
                            span: the button keeps focus and its tooltip. */}
                        <span
                          key={count}
                          aria-hidden
                          className={`inline-flex items-center gap-1 ${
                            pops ? MOTION.pop : ''
                          }`}
                        >
                          {reaction.emoji} {count}
                        </span>
                      </button>
                    ) : (
                      <span tabIndex={0} className={chipClass(mine)}>
                        {/* No aria-hidden: this text is the chip's whole label. */}
                        <span
                          key={count}
                          className={`inline-flex items-center gap-1 ${
                            pops ? MOTION.pop : ''
                          }`}
                        >
                          {reaction.emoji} {count}
                        </span>
                      </span>
                    )}
                  </TooltipTrigger>
                  <TooltipContent className={MOTION.reduceRadix}>
                    {reactorNames(reaction, currentUserId)}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        )}
        {touch && selected && !removed && reactions.length > 0 && (
          <p className='mt-0.5 text-[11px] text-muted-foreground'>
            {reactions
              .map(r => `${r.emoji} ${reactorNames(r, currentUserId)}`)
              .join(' · ')}
          </p>
        )}
        <span className='mt-0.5 text-[11px] text-muted-foreground'>
          {new Date(message.created_at).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          })}
          {!removed && message.edited_at ? ' (edited)' : null}
        </span>
      </div>
    </div>
  );
}
